import { useState, useEffect } from 'react'
import * as Sentry from '@sentry/react'
import useAuthStore from '../../common/store/useAuthStore'
import HeaderInfo from './components/HeaderInfo'
import DateRangeSelector from './components/DateRangeSelector'
import InformeTabs from './components/InformeTabs'
import EmptyMessage from './components/EmptyMessage'
import AlertMessage from '../../common/components/AlertMessage'
import { useAlert } from '../../common/utils/hooks/useAlert'
import { ALERT_MESSAGES } from '../../common/constants/labels'
import { generateInformePDF } from './utils/generatePDF'
import { generateReporteInsiSinDatos } from './api/informesApi'
import { getCompanyDatos } from './api/companyApi'
import { useInformeData } from './hooks/useInformeData'
import { captureInformeAssets } from './utils/captureAssets'
import { formatPeriodo } from './utils/informe/helpers'

const Home = () => {
  const user = useAuthStore((state) => state.user)
  const isRefreshingSession = useAuthStore((state) => state.isRefreshing)
  const refreshSessionData = useAuthStore((state) => state.refreshSessionData)
  const [reportData, setReportData] = useState(null)
  const [periodoData, setPeriodoData] = useState(null)
  const [reportContrato, setReportContrato] = useState(null)
  const [reportTrigger, setReportTrigger] = useState(0)
  const [showDownloadButton, setShowDownloadButton] = useState(false)
  const [isDownloading, setIsDownloading] = useState(false)

  const alert = useAlert()
  const { showError } = alert

  const {
    isLoading,
    hasData,
    isChecking,
    hasCheckError,
    hasServiceError,
    topDenunciantes,
    topJudiciales,
    topEvolucionSiniestros,
    topRankingSiniestros,
    indicadores,
    graficosTipoAccidente,
    graficosGravedad,
    graficosDenuncias,
  } = useInformeData(reportTrigger, reportData, periodoData, reportContrato)

  useEffect(() => {
    if (hasCheckError && !isChecking) {
      showError(ALERT_MESSAGES.errorMsj)
    }
  }, [hasCheckError, isChecking, showError])

  useEffect(() => {
    if (hasServiceError) {
      showError(ALERT_MESSAGES.errorMsj)
    }
  }, [hasServiceError, showError])

  useEffect(() => {
    if (!reportData || isChecking || hasCheckError) {
      setShowDownloadButton(false)
      return
    }

    if (hasData) {
      // Con datos: esperar a que el cuerpo del informe esté listo antes de descargar.
      setShowDownloadButton(!topDenunciantes.isLoading)
      return
    }

    // Reporte "sin siniestros": la descarga está disponible ni bien se genera.
    setShowDownloadButton(true)
  }, [reportData, isChecking, hasCheckError, hasData, topDenunciantes.isLoading])

  const handleGenerateReport = async (dateRange) => {
    alert.hideAlert()

    try {
      const refreshedSessionData = await refreshSessionData()
      const contrato = refreshedSessionData?.SE_CONTRATO ?? useAuthStore.getState().user?.contrato ?? user?.contrato

      const [toYear, toMonth] = dateRange.fechaHasta.split('-').map(Number)
      const lastDay = new Date(toYear, toMonth, 0).getDate()

      setPeriodoData({
        fechaDesde: dateRange.fechaDesde.replace('-', ''),
        fechaHasta: dateRange.fechaHasta.replace('-', ''),
      })

      setReportData({
        fechaDesde: `${dateRange.fechaDesde}-01`,
        fechaHasta: `${dateRange.fechaHasta}-${String(lastDay).padStart(2, '0')}`,
      })
      setReportContrato(contrato)
      setReportTrigger((prev) => prev + 1)
      setShowDownloadButton(false)
    } catch {
      alert.showError(ALERT_MESSAGES.errorMsj)
    }
  }

  const getRazonSocial = async () => {
    // Company se consulta SOLO al momento de la descarga. Esperamos la respuesta
    // antes de generar el PDF para no emitir un encabezado vacío.
    const { data } = await getCompanyDatos(reportContrato ?? user?.contrato)
    return data?.NOMBREEMPRESA ?? ''
  }

  const buildPayload = (assets, razonSocialFinal) => ({
    reportData,
    empleador: {
      razonSocial: razonSocialFinal,
      contrato: reportContrato ?? user?.contrato,
      cuit: user?.cuit,
    },
    topDenunciantes: topDenunciantes.data,
    topJudiciales: topJudiciales.data,
    topEvolucionSiniestros: topEvolucionSiniestros.data,
    indicadores: indicadores.data,
    topRankingSiniestros: topRankingSiniestros.data,
    ...assets,
  })

  const handleDownloadSinDatos = async () => {
    if (!periodoData) return

    try {
      const response = await generateReporteInsiSinDatos({
        contrato: reportContrato ?? user?.contrato,
        periodoDesde: formatPeriodo(periodoData.fechaDesde),
        periodoHasta: formatPeriodo(periodoData.fechaHasta),
      })

      const reportUrl = response.data?.url
      if (reportUrl) {
        window.open(reportUrl, '_blank', 'noopener,noreferrer')
      }

      alert.showSuccess(ALERT_MESSAGES.successMsj)
    } catch (error) {
      Sentry.captureException(error, {
        tags: { feature: 'reporting', action: 'download-sin-datos' },
      })
      alert.showError(ALERT_MESSAGES.errorMsj)
    }
  }

  const handleDownload = async () => {
    if (isDownloading) return
    setIsDownloading(true)
    try {
      const razonSocialFinal = await getRazonSocial()
      const assets = await captureInformeAssets(graficosDenuncias.data?.categories?.length > 0)
      await generateInformePDF(buildPayload(assets, razonSocialFinal))
    } catch (error) {
      Sentry.captureException(error)
      alert.showError(ALERT_MESSAGES.errorMsj)
    } finally {
      setIsDownloading(false)
    }
  }

  return (
    <div className="w-full">
      <div className="py-6">
        <HeaderInfo />

        <DateRangeSelector
          onGenerateReport={handleGenerateReport}
          isLoading={isLoading || isRefreshingSession}
          showDownloadButton={showDownloadButton}
          onDownload={hasData ? handleDownload : handleDownloadSinDatos}
          isDownloading={isDownloading}
        />

        <AlertMessage
          show={alert.show}
          variant={alert.variant}
          message={alert.message}
          onClose={alert.hideAlert}
          floating={true}
        />

        {isChecking && !hasData && !hasCheckError && (
          <div className="rounded-md p-4 bg-tertiary-98 mt-6">
            <div className="flex justify-center items-center py-12">
              <div className="h-8 w-8 animate-spin rounded-full border-4 border-gray-200 border-t-blue-primary"></div>
            </div>
          </div>
        )}

        {hasData && (
          <InformeTabs
            data={reportData}
            topDenunciantes={topDenunciantes.data}
            topDenunciantesLoading={topDenunciantes.isLoading}
            topJudiciales={topJudiciales.data}
            topJudicialesLoading={topJudiciales.isLoading}
            topEvolucionSiniestros={topEvolucionSiniestros.data}
            topEvolucionSiniestrosLoading={topEvolucionSiniestros.isLoading}
            topRankingSiniestros={topRankingSiniestros.data}
            topRankingSiniestrosLoading={topRankingSiniestros.isLoading}
            indicadores={indicadores.data}
            indicadoresLoading={indicadores.isLoading}
            graficosTipoAccidente={graficosTipoAccidente.data}
            graficosTipoAccidenteLoading={graficosTipoAccidente.isLoading}
            graficosGravedad={graficosGravedad.data}
            graficosGravedadLoading={graficosGravedad.isLoading}
            graficosDenuncias={graficosDenuncias.data}
            graficosDenunciasLoading={graficosDenuncias.isLoading}
            graficosDenunciasShouldRender={graficosDenuncias.shouldRender}
          />
        )}

        {!isChecking && reportData && !hasData && !hasCheckError && (
          <EmptyMessage
            periodoData={periodoData}
            contrato={reportContrato ?? user?.contrato}
            onError={alert.showError}
            onSuccess={alert.showSuccess}
          />
        )}
      </div>
    </div>
  )
}

export default Home
